/** A teacher picks which hat or class the panel's figures are for: both hats, homeroom,
 *  their subjects, or one class ('section:<id>', or 'grade:<id>' for a grade they teach
 *  grade-wide). Sent with every request as X-View-As (src/utils/constant.tsx); the server
 *  ignores a choice the teacher holds no assignment for. */
export type ViewAs = 'both' | 'homeroom' | 'subjects'
  | `section:${number}` | `grade:${number}`
  | `section:${number}:${'homeroom' | 'subjects'}` | `grade:${number}:${'homeroom' | 'subjects'}`;

export function isViewAs(v: unknown): v is ViewAs {
  return v === 'both' || v === 'homeroom' || v === 'subjects'
    || (typeof v === 'string' && /^(section|grade):\d{1,12}(:(homeroom|subjects))?$/.test(v));
}

const KEY = 'yada.viewAs';
const listeners = new Set<(v: ViewAs) => void>();

export function getViewAs(): ViewAs {
  try {
    const v = localStorage.getItem(KEY);
    return isViewAs(v) ? v : 'both';
  } catch {
    return 'both';
  }
}

export function setViewAs(v: ViewAs): void {
  try {
    if (v === 'both') localStorage.removeItem(KEY); else localStorage.setItem(KEY, v);
  } catch { /* storage blocked: the choice lasts for this page only */ }
  memory = v;
  listeners.forEach((fn) => fn(v));
}

let memory: ViewAs | null = null;

/** The header value to send; null when nothing narrows the view. */
export function viewAsHeader(): ViewAs | null {
  const v = memory ?? getViewAs();
  return v === 'both' ? null : v;
}

export function onViewAsChange(fn: (v: ViewAs) => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}
